import { useQuery, type QueryState } from "../analytics";
import { workspaceKeyPrefix } from "../identity/active";
import { useAgentClient } from "./client";
import type { Agent, AgentOptimization } from "./types";

/* Reads use the analytics useQuery cache, keyed under the active workspace
 * (the gateway scopes agents by X-Workspace-Id). Mutations don't go through
 * here — callers invoke the client directly then bump `version` to force a
 * refetch (a new cache key), so the list reflects the change without a full
 * reload. */

export function useAgents(version = 0): QueryState<Agent[]> {
  const client = useAgentClient();
  return useQuery(
    () => client.list(),
    `${workspaceKeyPrefix()}agents:v${version}`,
    "loadAgents",
  );
}

export function useAgent(agentId: string, version = 0): QueryState<Agent> {
  const client = useAgentClient();
  return useQuery(
    () => client.get(agentId),
    `${workspaceKeyPrefix()}agent:${agentId}:v${version}`,
    "loadAgents",
  );
}

/** The optimizations curated for an agent (OPSS-152), for the agent page. */
export function useAgentOptimizations(
  agentId: string,
  version = 0,
): QueryState<AgentOptimization[]> {
  const client = useAgentClient();
  return useQuery(
    () => client.listOptimizations(agentId),
    `${workspaceKeyPrefix()}agent-optimizations:${agentId}:v${version}`,
    "loadAgents",
  );
}
